import React, { memo, useState } from 'react';
import clsx from 'clsx';
import HeaderCell from './HeaderCell';
import { getColSpan } from './utils';
import { headerRowClassname, rowReorderTargetClassname } from './style';
import type { CalculatedColumn, DragPosition } from './types';
import type { DataGridProps } from './DataGrid';

type SharedDataGridProps<R, SR, K extends React.Key> = Pick<
  DataGridProps<R, SR, K>,
  'sortColumns' | 'onSortColumnsChange'
>;

export interface HeaderRowProps<R, SR, K extends React.Key> extends SharedDataGridProps<R, SR, K> {
  columns: readonly CalculatedColumn<R, SR>[];
  allRowsSelected: boolean;
  onAllRowsSelectionChange: (checked: boolean) => void;
  lastFrozenColumnIndex: number;
  onColumnResize: (column: CalculatedColumn<R, SR>, width: number, mouseDown: boolean) => void;
  handleHeaderCellClick: (idx: number) => void;
  dragPos: React.MutableRefObject<DragPosition<R, SR> | undefined>;
}

function HeaderRow<R, SR, K extends React.Key>({
  allRowsSelected,
  columns,
  dragPos,
  handleHeaderCellClick,
  lastFrozenColumnIndex,
  onAllRowsSelectionChange,
  onColumnResize,
  onSortColumnsChange,
  sortColumns,
}: HeaderRowProps<R, SR, K>) {
  const cells = [];
  const [dragHover, setDragHover] = useState<boolean>(false);
  const isReordering = dragPos.current?.selType === "REORDER" && dragHover;

  for (let index = 0; index < columns.length; index++) {
    const column = columns[index];
    const colSpan = getColSpan(column, lastFrozenColumnIndex, { type: 'HEADER' });
    if (colSpan !== undefined) {
      index += colSpan - 1;
    }

    cells.push(
      <HeaderCell<R, SR>
        allRowsSelected={allRowsSelected}
        column={column}
        colSpan={colSpan}
        dragPos={dragPos}
        handleClick={handleHeaderCellClick}
        isReordering={isReordering}
        key={column.key}
        onAllRowsSelectionChange={onAllRowsSelectionChange}
        onResize={onColumnResize}
        onSortColumnsChange={onSortColumnsChange}
        sortColumns={sortColumns}
      />
    );
  }

  const headerRowClasses = clsx(
    headerRowClassname,
    {
      [rowReorderTargetClassname]: isReordering
    }
  );

  function handleDragEnter() {
    setDragHover(true);
    //dragPos.current = undefined;
  }

  function handleDragLeave(event: React.MouseEvent<HTMLDivElement>) {
    setDragHover(false);
  }

  function handleMouseUp(event: React.MouseEvent<HTMLDivElement>) {
    setDragHover(false);
  }

  return (
    <div
      aria-rowindex={1} // aria-rowindex is 1 based
      className={headerRowClasses}
      onMouseEnter={handleDragEnter}
      onMouseLeave={handleDragLeave}
      onMouseUp={handleMouseUp}
      role="row"
    >
      {cells}
    </div>
  );
}

export default memo(HeaderRow) as <R, SR, K extends React.Key>(
  props: HeaderRowProps<R, SR, K>
) => JSX.Element;
